/* eslint-disable @typescript-eslint/no-unused-vars */
import _ from 'lodash';
import { AsyncThunk, createAsyncThunk, Dispatch } from '@reduxjs/toolkit';
import { ApiResponse, Configuration, ResponseError } from 'generated/api';
import {
  ApiInstance,
  EXTRA_HEADERS,
  OperationFunction,
  OperationId,
  OpFirstParam,
  ResponseType,
  SimpleApiKey,
  simplifiedApi,
  SimplifiedApi,
} from './ApiTypeUtils';
import { defaultErrorMiddleware } from './ApiThunkMiddleware';
import moment from 'moment-timezone';
import { serialize } from 'careand-redux/api/SerializedReponseError';
import apiVersion from 'ApiVersion.json';
import config from 'config';
import { endpointIsLoading } from 'careand-redux/actions/endpointLoading';
import PerformanceUtils from 'services/PerformanceUtils';
import type { InitOverrideFunction, RequestOpts } from 'generated/api';
import version from 'config/version.json';
import { TenantId } from '../selectors';

type ApiThunkConfig = {
  dispatch: Dispatch;
  state: unknown;
  fulfilledMeta: ApiMeta;
  rejectedMeta: ApiMeta;
};

export type ApiThunk<Tag extends SimpleApiKey, OpId extends OperationId<Tag>> = AsyncThunk<
  ResponseType<Tag, OpId>,
  OpFirstParam<OperationFunction<Tag, OpId>>,
  ApiThunkConfig
>;

type TagThunks<Tag extends SimpleApiKey> = {
  [OpId in OperationId<Tag>]: ApiThunk<Tag, OpId>;
};

type AllThunks = {
  [Tag in SimpleApiKey]: TagThunks<Tag>;
};

type MemoizedApis = {
  [Tag in keyof SimplifiedApi]: ApiInstance<Tag>;
};

export type RequestOptions = RequestOpts & {
  basePath?: string;
};

type ApiRequestConfigGetter<Tag extends SimpleApiKey, OpId extends OperationId<Tag>> = (
  args: OpFirstParam<OperationFunction<Tag, OpId>>,
  extraHeaders?: Record<string, string | undefined>,
) => RequestOptions;

export type AllApiRequestConfigGetters = {
  [Tag in SimpleApiKey]: {
    [OpId in OperationId<Tag>]: ApiRequestConfigGetter<Tag, OpId>;
  };
};

/* RUNTIME */

import { Device } from '../../services/Native/Device';
import { store } from '../store';

const { API_URL, PROTOCOL, TENANT_ID } = config;

const API_KEY_HOLDER: { apiKey?: string } = {
  apiKey: undefined,
};

export function UNSAFE_withTenantId<T>(id: string, callback: () => T) {
  const old = globalApiConfig.headers?.['tenant_id'];

  function revert() {
    if (old) globalApiConfig.headers!['tenant_id'] = old!;
    else delete globalApiConfig.headers!['tenant_id'];
  }

  globalApiConfig.headers!['tenant_id'] = id;

  const res = callback();

  if (res instanceof Promise) {
    return res.finally(revert);
  } else {
    revert();
    return res;
  }
}

const basePath =
  API_URL === 'localhost:5000' && Device.isAndroid()
    ? `${PROTOCOL}://10.0.2.2/api`
    : `${PROTOCOL}://${API_URL}/api`;

export const globalApiConfig = new Configuration({
  basePath,
  apiKey: (name: string) => API_KEY_HOLDER.apiKey ?? '',
  headers: {
    'api-version': apiVersion.httpApiVersion,
    'client-version': version['client-version'],
    // TODO portal needs to come from redux and needs to be auto updated
    'client-name': 'portal',
  },
});

export function updateApiHeaders(newHeaders: Record<string, string | undefined>) {
  Object.entries(newHeaders).forEach(([key, val]) => {
    if (val) {
      globalApiConfig.headers![key] = val;
    } else if (key in globalApiConfig.headers!) {
      delete globalApiConfig.headers![key];
    }
  });
}

export const updateTrackingHeaders = (headers: Record<string, string> = {}) => {
  Object.keys(headers).forEach((key) => {
    let value = headers[key];
    if (value) {
      globalApiConfig.headers![key] = value;
    }
  });
};

const memoizedApis = ((): MemoizedApis => {
  const result = _.mapValues(simplifiedApi, (Klass) => new Klass(globalApiConfig));
  return result as MemoizedApis;
})();

export type ApiMeta = { status: number };

// 2. Generate Thunks
const generateSingleThunk = <Tag extends SimpleApiKey, OpId extends OperationId<Tag>>(
  tag: Tag,
  opId: OpId,
): ApiThunk<Tag, OpId> => {
  return createAsyncThunk<
    ResponseType<Tag, OpId>,
    OpFirstParam<OperationFunction<Tag, OpId>>,
    ApiThunkConfig
  >(`${tag}/${opId}`, async (params, thunksApi) => {
    const typePrefix = `${tag}/${opId}`;
    const token = (thunksApi.getState() as any)?.authentication?.token;
    API_KEY_HOLDER.apiKey = token ? `Bearer ${token}` : undefined;

    //insert extra headers as needed
    let initOverride: InitOverrideFunction | undefined;
    if (params && params[EXTRA_HEADERS]) {
      const extraHeaders = params[EXTRA_HEADERS];
      params = _.omit(params, EXTRA_HEADERS) as any;
      initOverride = async (ctx) => {
        const result = {
          ...ctx.init,
          headers: {
            ...(ctx.init.headers ?? {}),
            ...extraHeaders,
          },
        };
        return result;
      };
    }

    const time = moment();
    let url = 'url?';
    let status = -1;
    let rejectValue: unknown;

    thunksApi.dispatch(endpointIsLoading({ name: typePrefix, params }));

    try {
      const api = memoizedApis[tag];
      const reqParams = params === undefined ? [] : [params];
      const response = (await (api as any)[`${opId}Raw`](
        ...reqParams,
        initOverride,
      )) as ApiResponse<ResponseType<Tag, OpId>>;
      url = response.raw.url;
      status = response.raw.status;

      const meta = { status };

      if (status === 204) {
        return thunksApi.fulfillWithValue(undefined, meta);
      }
      const value = (await response.value()) as ResponseType<Tag, OpId>;

      PerformanceUtils.removeUndefinedKeys(value);

      return thunksApi.fulfillWithValue(value, meta) as any;
    } catch (err: unknown) {
      if (err instanceof ResponseError) {
        url = err.response.url;
        status = err.response.status;

        const serializedError = await serialize(err);
        rejectValue = serializedError;
      } else {
        rejectValue = err;
      }
      const meta = { status };
      defaultErrorMiddleware(err, thunksApi.getState as any, thunksApi.dispatch as any);
      return thunksApi.rejectWithValue(rejectValue, meta);
    } finally {
      const delta = moment().diff(time, 'milliseconds');
      thunksApi.dispatch(endpointIsLoading({ name: typePrefix, params, loading: false }));
    }
  });
};

const generateTagThunks = <Tag extends SimpleApiKey>(tag: Tag) => {
  const allMethodNames = Object.getOwnPropertyNames(memoizedApis[tag].constructor.prototype);
  const ops: OperationId<Tag>[] = _(allMethodNames)
    .filter(
      (it) =>
        it !== 'constructor' &&
        !it.endsWith('Raw') &&
        !(it.startsWith('with') && it.endsWith('Middleware')),
    )
    .value() as OperationId<Tag>[];
  const res: Partial<TagThunks<Tag>> = {};
  ops.forEach((opId) => {
    res[opId] = generateSingleThunk(tag, opId);
  });
  return res as TagThunks<Tag>;
};
const generateAllThunks = (): AllThunks => {
  const result = _.mapValues(memoizedApis, (value, key) => generateTagThunks(key as SimpleApiKey));
  return result as AllThunks;
};

const generateAllRequestConfigGetters = (): AllApiRequestConfigGetters => {
  function generateForTag<Tag extends SimpleApiKey>(tag: Tag): AllApiRequestConfigGetters[Tag] {
    const allMethodNames = Object.getOwnPropertyNames(memoizedApis[tag].constructor.prototype);
    const ops: OperationId<Tag>[] = _(allMethodNames)
      .filter(
        (it) =>
          it !== 'constructor' &&
          !it.endsWith('Raw') &&
          !(it.startsWith('with') && it.endsWith('Middleware')),
      )
      .value() as OperationId<Tag>[];
    const res: Partial<AllApiRequestConfigGetters[Tag]> = {};
    ops.forEach((opId) => {
      res[opId] = ((args: any, extraHeaders?: Record<string, string | undefined>) => {
        return {
          ...(memoizedApis[tag] as any).constructor.prototype[`${opId}RequestOptionsRaw`](args),
          basePath: `${PROTOCOL}://${API_URL}/api`,
          headers: {
            ...globalApiConfig.headers,
            ...(extraHeaders ?? {}),
          },
        };
      }) as any;
    });
    return res as AllApiRequestConfigGetters[Tag];
  }
  const result = _.mapValues(memoizedApis, (value, key) => generateForTag(key as SimpleApiKey));

  return result as AllApiRequestConfigGetters;
};

export const api = generateAllThunks();

export const apiRequestConfig = generateAllRequestConfigGetters();
